/**
 * Analytics Page - Comprehensive cross-brand analytics view
 * Displays detailed performance metrics, trends, and breakdowns
 */

import React, { useState, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Tabs,
  Select,
  Button,
  Grid,
  Spinner,
  Banner,
  Divider,
  Icon,
  Tooltip,
  TextField,
  Modal,
  FormLayout,
} from '@shopify/polaris';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  CashDollarIcon,
  OrderIcon,
  StoreIcon,
  ChartVerticalIcon,
  CalendarIcon,
  FilterIcon,
  ReceiptRefundIcon,
  ReturnIcon,
} from '@shopify/polaris-icons';

// Import analytics components
import MetricCard, { RevenueCard, CommissionCard, OrdersCard } from '../components/Analytics/MetricCard';
import CommissionBreakdownCard from '../components/Analytics/CommissionBreakdownCard';
import MarketplaceBreakdown from '../components/Analytics/MarketplaceBreakdown';
import { useBrands, useAnalyticsOverview, useDetailedAnalytics } from '../utils/queries';
import apiClient from '../utils/api';

// Helper function - formats date as YYYY-MM-DD without timezone shift
// This avoids the bug where toISOString() converts to UTC, shifting dates by 1 day
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Analytics() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeFilter, setTimeFilter] = useState('30'); // days
  console.log('timeFilter: ', timeFilter);
  const [brandFilter, setBrandFilter] = useState('all');
  const [comparisonPeriod, setComparisonPeriod] = useState('previous');

  // Custom date range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customDateRange, setCustomDateRange] = useState(null);

  // Query hooks for data fetching with safe data access
  const { data: brandsResponse, isLoading: brandsLoading, error: brandsError } = useBrands();
  const brands = useMemo(() => {
    try {
      if (Array.isArray(brandsResponse)) {
        return brandsResponse.filter(brand => brand && brand.id);
      }
      if (brandsResponse && Array.isArray(brandsResponse.brands)) {
        return brandsResponse.brands.filter(brand => brand && brand.id);
      }
      return [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error processing brands data:', error);
      }
      return [];
    }
  }, [brandsResponse]);
  const {
    data: analyticsOverview,
    isLoading: overviewLoading,
    error: overviewError
  } = useAnalyticsOverview();
  // Build analytics query parameters
  const analyticsParams = useMemo(() => {
    const params = { brand: brandFilter };

    if (timeFilter === 'custom' && customDateRange) {
      // For custom date range, pass actual dates
      params.startDate = customDateRange.startDate;
      params.endDate = customDateRange.endDate;

      // Also calculate days for backward compatibility
      const start = new Date(customDateRange.startDate);
      const end = new Date(customDateRange.endDate);
      const diffTime = Math.abs(end - start);
      params.days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } else {
      // Handle preset date ranges
      const now = new Date();
      switch (timeFilter) {
        case 'mtd': // Month to Date
          params.startDate = formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1));
          params.endDate = formatDateLocal(now);
          params.days = now.getDate();
          break;
        case 'ytd': // Year to Date
          const startOfYear = new Date(now.getFullYear(), 0, 1);
          params.startDate = formatDateLocal(startOfYear);
          params.endDate = formatDateLocal(now);
          params.days = Math.ceil((now - startOfYear) / (1000 * 60 * 60 * 24));
          break;
        case 'this_month': // This Month
          params.startDate = formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1));
          params.endDate = formatDateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0));
          params.days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
          break;
        case 'last_month': // Last Month
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
          params.startDate = formatDateLocal(lastMonth);
          params.endDate = formatDateLocal(lastMonthEnd);
          params.days = lastMonthEnd.getDate();
          break;
        default:
          // For simple day counts, calculate start date
          const parsed = parseInt(timeFilter, 10);
          const daysCount = isNaN(parsed) || parsed <= 0 ? 30 : Math.min(parsed, 999999);
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - daysCount);
          params.startDate = formatDateLocal(startDate);
          params.endDate = formatDateLocal(now);
          params.days = daysCount;
      }
    }

    return params;
  }, [brandFilter, timeFilter, customDateRange]);

  const {
    data: detailedAnalytics,
    isLoading: detailedLoading,
    error: detailedError
  } = useDetailedAnalytics(analyticsParams);

  // Filter options
  const timeFilterOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Month to Date', value: 'mtd' },
    { label: 'Year to Date', value: 'ytd' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'Last 365 days', value: '365' },
    { label: 'Custom Date Range', value: 'custom' },
  ];

  const brandFilterOptions = useMemo(() => {
    const options = [{ label: 'All Brands', value: 'all' }];
    if (Array.isArray(brands)) {
      brands.forEach(brand => {
        if (brand && brand.id && brand.name) {
          options.push({
            label: brand.name,
            value: brand.id
          });
        }
      });
    }
    return options;
  }, [brands]);

  // Custom date range functions
  const handleTimeFilterChange = (value) => {
    if (value === 'custom') {
      setShowCustomDateModal(true);
    } else {
      setTimeFilter(value);
      setCustomDateRange(null);
    }
  };

  const handleCustomDateSubmit = () => {
    if (customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);

      if (startDate <= endDate) {
        setCustomDateRange({
          startDate: customStartDate,
          endDate: customEndDate
        });
        setTimeFilter('custom');
        setShowCustomDateModal(false);
      }
    }
  };

  const formatCustomDateLabel = () => {
    if (customDateRange) {
      const startDate = new Date(customDateRange.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const endDate = new Date(customDateRange.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return `${startDate} - ${endDate}`;
    }
    return 'Custom Date Range';
  };

  // Calculate performance metrics with error boundaries
  const performanceMetrics = useMemo(() => {
    try {
      if (!detailedAnalytics || typeof detailedAnalytics !== 'object') return null;

      return detailedAnalytics;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error processing performance metrics:', error);
      }
      return null;
    }
  }, [detailedAnalytics]);

  // Tab configuration
  const tabs = [
    {
      id: 'overview',
      content: 'Performance Overview',
      panelID: 'overview-panel',
      accessibilityLabel: 'View performance overview and key metrics'
    },
    {
      id: 'marketplace',
      content: 'Marketplace Analysis',
      panelID: 'marketplace-panel',
      accessibilityLabel: 'View marketplace performance breakdown'
    },
  ];


  // Render performance overview tab with error boundary
  const renderOverviewTab = () => {
    try {
      return (
        <Layout>
          {/* Key Metrics Cards */}
          <Layout.Section>
            <Grid>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <OrdersCard
                  title="Orders"
                  value={performanceMetrics?.totalOrders || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`AOV: ${(() => {
                  //   try {
                  //     return apiClient.formatCurrency(performanceMetrics?.averageOrderValue || 0);
                  //   } catch (error) {
                  //     return '$0.00';
                  //   }
                  // })()}`}
                  icon={OrderIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <RevenueCard
                  title="Gross Revenue"
                  value={performanceMetrics?.totalGrossRevenue || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  tooltip={"Total revenue generated across all brands in the selected time period. Calculated from fulfilled orders."}
                  // change={performanceMetrics && {
                  //   percentage: performanceMetrics.monthlyGrowth,
                  //   direction: performanceMetrics.monthlyGrowth >= 0 ? 'up' : 'down',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`Across ${performanceMetrics?.activeBrandsCount || 0} brands`}
                  icon={CashDollarIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <MetricCard
                  title="Discounts"
                  value={performanceMetrics?.totalDiscounts || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle="Total amount refunded"
                  icon={ReceiptRefundIcon}
                  color="critical"
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <MetricCard
                  title="Refunds"
                  value={performanceMetrics?.totalRefunds || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle="Total amount refunded"
                  icon={ReceiptRefundIcon}
                  color="critical"
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <RevenueCard
                  title="Net Sales"
                  value={performanceMetrics?.totalRevenue || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  tooltip={"Total sales after excluding discounts and refunds."}
                  // change={performanceMetrics && {
                  //   percentage: performanceMetrics.monthlyGrowth,
                  //   direction: performanceMetrics.monthlyGrowth >= 0 ? 'up' : 'down',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`Across ${performanceMetrics?.activeBrandsCount || 0} brands`}
                  icon={CashDollarIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <CommissionBreakdownCard
                  commissionBreakdown={(() => {
                    try {
                      return performanceMetrics?.commissionBreakdown || null;
                    } catch (error) {
                      if (process.env.NODE_ENV === 'development') {
                        console.error('Analytics: Error accessing commission breakdown:', error);
                      }
                      return null;
                    }
                  })()}
                  loading={detailedLoading}
                  error={detailedError}
                  // change={performanceMetrics && {
                  //   percentage: performanceMetrics.commissionGrowth,
                  //   direction: performanceMetrics.commissionGrowth >= 0 ? 'up' : 'down',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  color="success"
                  tooltip="Commission breakdown: base commission + adjustments = total. Shows transparency of commission composition."
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <MetricCard
                  title="Shipping Fees"
                  value={performanceMetrics?.totalShippingFees || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`${performanceMetrics?.totalMarketplaces || 0} marketplaces`}
                  icon={StoreIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <MetricCard
                  title="Return Fees"
                  value={performanceMetrics?.totalReturnFees || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle="From refund processing"
                  icon={ReturnIcon}
                  color="warning"
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 3 }}>
                <RevenueCard
                  title="Net Payout"
                  value={performanceMetrics?.totalPayout || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  tooltip={"Total payout merchant will get after all discounts, refunds, commission, returns and shipping fees."}
                  // change={performanceMetrics && {
                  //   percentage: performanceMetrics.monthlyGrowth,
                  //   direction: performanceMetrics.monthlyGrowth >= 0 ? 'up' : 'down',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`Across ${performanceMetrics?.activeBrandsCount || 0} brands`}
                  icon={CashDollarIcon}
                />
              </Grid.Cell>
            </Grid>
          </Layout.Section>

        </Layout>
      );
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error rendering overview tab:', error);
      }
      return (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to display performance overview. Please refresh the page.</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      );
    }
  };

  // Render marketplace analysis tab with error boundary
  const renderMarketplaceTab = () => {
    try {
      return (
        <Layout>
          <Layout.Section>
            <MarketplaceBreakdown
              brandId={brandFilter}
              period={analyticsParams.days || 30}
              dateRange={analyticsParams.startDate && analyticsParams.endDate ? {
                startDate: analyticsParams.startDate,
                endDate: analyticsParams.endDate
              } : null}
              title="Cross-Brand Marketplace Analysis"
              showFilters={true}
              onMarketplaceSelect={(marketplace) => {
                if (process.env.NODE_ENV === 'development') {
                  console.log('Analytics: Selected marketplace for detailed view:', marketplace);
                }
              }}
            />
          </Layout.Section>
        </Layout>
      );
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error rendering marketplace tab:', error);
      }
      return (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to display marketplace analysis. Please refresh the page.</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      );
    }
  };



  // Global loading and error state
  if (overviewError) {
    return (
      <Page title="Analytics" backAction={{ content: 'Dashboard', url: '/' }} fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load analytics overview. {overviewError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (brandsError && !brands.length) {
    return (
      <Page title="Analytics" backAction={{ content: 'Dashboard', url: '/' }} fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load brands data. {brandsError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (brandsLoading || overviewLoading) {
    return (
      <Page title="Analytics" backAction={{ content: 'Dashboard', url: '/' }} fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">
                      Loading analytics data...
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      Fetching cross-brand performance metrics
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Cross-Brand Analytics"
      fullWidth
      subtitle={`Performance insights across ${brands.length} brand${brands.length !== 1 ? 's' : ''}`}
      backAction={{ content: 'Dashboard', url: '/' }}
      compactTitle
    >
      {detailedError && (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>{detailedError.message || 'Failed to load analytics data'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      )}

      <Layout>
        {/* Filters */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineStack gap="400" align="start" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={CalendarIcon} />
                  <Select
                    label=""
                    options={timeFilter === 'custom' ? [
                      ...timeFilterOptions.filter(opt => opt.value !== 'custom'),
                      { label: formatCustomDateLabel(), value: 'custom' }
                    ] : timeFilterOptions}
                    value={timeFilter}
                    onChange={handleTimeFilterChange}
                  />
                </InlineStack>

                <InlineStack gap="200" blockAlign="center">
                  <Icon source={FilterIcon} />
                  <Select
                    label=""
                    options={brandFilterOptions}
                    value={brandFilter}
                    onChange={setBrandFilter}
                  />
                </InlineStack>

                <Text variant="bodySm" tone="subdued">
                  Showing data for {
                    timeFilter === '999999' ? 'all time' :
                      timeFilter === 'custom' ? formatCustomDateLabel() :
                        timeFilter === 'mtd' ? 'month to date' :
                          timeFilter === 'ytd' ? 'year to date' :
                            timeFilter === 'this_month' ? 'this month' :
                              timeFilter === 'last_month' ? 'last month' :
                                `last ${timeFilter} days`
                  }
                  {brandFilter !== 'all' && (() => {
                    try {
                      if (!Array.isArray(brands) || brands.length === 0) {
                        return ' • Selected brand';
                      }
                      const selectedBrand = brands.find(b => b && typeof b === 'object' && b.id === brandFilter);
                      return selectedBrand && selectedBrand.name ? ` • ${selectedBrand.name}` : ' • Selected brand';
                    } catch (error) {
                      if (process.env.NODE_ENV === 'development') {
                        console.error('Analytics: Error finding selected brand:', error);
                      }
                      return ' • Selected brand';
                    }
                  })()}
                </Text>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Tabs */}
        <Layout.Section>
          <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
            <Box paddingBlockStart="600">
              {selectedTab === 0 && renderOverviewTab()}
              {selectedTab === 1 && renderMarketplaceTab()}
            </Box>
          </Tabs>
        </Layout.Section>
      </Layout>

      {/* Custom Date Range Modal */}
      <Modal
        open={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        title="Select Custom Date Range"
        primaryAction={{
          content: 'Apply Date Range',
          onAction: handleCustomDateSubmit,
          disabled: !customStartDate || !customEndDate,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowCustomDateModal(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Text variant="bodyMd" tone="subdued">
              Select a custom date range for analytics data. The range will be applied to all metrics and charts.
            </Text>
            <FormLayout.Group>
              <TextField
                label="Start Date"
                type="date"
                value={customStartDate}
                onChange={setCustomStartDate}
                helpText="Select the start date for your analysis"
              />
              <TextField
                label="End Date"
                type="date"
                value={customEndDate}
                onChange={setCustomEndDate}
                helpText="Select the end date for your analysis"
                min={customStartDate}
              />
            </FormLayout.Group>
            {customStartDate && customEndDate && (
              <Box padding="300" background="bg-fill-info-secondary" borderRadius="200">
                <Text variant="bodySm">
                  Selected range: <Text variant="bodySm" fontWeight="medium">{formatCustomDateLabel()}</Text>
                </Text>
              </Box>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}